import React, { useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../contexts/AuthContext';
import {
    fetchCategories,
    addCategory as addCategoryService,
    updateCategory,
    deleteCategory as deleteCategoryService,
    fetchPostsByCategory,
    addPost,
    updatePost,
    deletePost as deletePostService,
    reorderPosts as reorderPostsService,
} from '../services/galleryService';
import { uploadImage as uploadImageToStorage, deleteImage } from '../services/storageService';
import '../styles/Admin.css';

// Sortable item component for drag-and-drop
const SortablePost = ({ post, onDelete, onEdit }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: post.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} className="admin-post-item">
            <div className="admin-post-drag" {...attributes} {...listeners}>
                ⋮⋮
            </div>
            <img src={post.image_url} alt={post.caption} className="admin-post-thumbnail" />
            <div className="admin-post-info">
                <p className="admin-post-caption">{post.caption}</p>
                <span className="admin-post-date">{post.date}</span>
            </div>
            <div className="admin-post-actions">
                <button
                    className="admin-post-edit"
                    onClick={() => onEdit(post)}
                >
                    ✏️
                </button>
                <button
                    className="admin-post-delete"
                    onClick={() => onDelete(post.id, post.image_url)}
                >
                    🗑️
                </button>
            </div>
        </div>
    );
};

const Admin = () => {
    const { user, signOut } = useAuth();

    // State management
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Category management state
    const [newCategoryName, setNewCategoryName] = useState('');
    const [renamingCategory, setRenamingCategory] = useState(null);
    const [renameValue, setRenameValue] = useState('');

    // Upload state
    const [uploadImage, setUploadImage] = useState(null);
    const [uploadCaption, setUploadCaption] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('');
    const [uploading, setUploading] = useState(false);

    // Edit state
    const [editingPost, setEditingPost] = useState(null);
    const [editCaption, setEditCaption] = useState('');
    const [editImage, setEditImage] = useState(null);
    const [editImagePreview, setEditImagePreview] = useState(null);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Load categories on mount
    useEffect(() => {
        loadCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load posts when category changes
    useEffect(() => {
        if (selectedCategory) {
            loadPosts(selectedCategory);
        }
    }, [selectedCategory]);

    const loadCategories = async () => {
        setLoading(true);
        const { data, error } = await fetchCategories();

        if (error) {
            setError('Failed to load categories: ' + error.message);
            setLoading(false);
            return;
        }

        setCategories(data || []);
        if (data && data.length > 0 && !selectedCategory) {
            setSelectedCategory(data[0].id);
        }
        setLoading(false);
    };

    const loadPosts = async (categoryId) => {
        const { data, error } = await fetchPostsByCategory(categoryId);

        if (error) {
            setError('Failed to load posts: ' + error.message);
            return;
        }

        setPosts(data || []);
    };

    const handleAddCategory = async () => {
        if (!newCategoryName.trim()) {
            alert('Please enter a category name');
            return;
        }

        const { data, error } = await addCategoryService(newCategoryName.trim());

        if (error) {
            alert('Failed to add category: ' + error.message);
            return;
        }

        setCategories([...categories, data]);
        setNewCategoryName('');

        // Auto-select the new category
        if (categories.length === 0) {
            setSelectedCategory(data.id);
        }
    };

    const handleRenameCategory = async (categoryId) => {
        if (!renameValue.trim()) {
            alert('Please enter a new category name');
            return;
        }

        const { data, error } = await updateCategory(categoryId, { name: renameValue.trim() });

        if (error) {
            alert('Failed to rename category: ' + error.message);
            return;
        }

        setCategories(categories.map(cat => cat.id === categoryId ? data : cat));
        setRenamingCategory(null);
        setRenameValue('');
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category? All posts in it will be removed.')) {
            return;
        }

        const { error } = await deleteCategoryService(categoryId);

        if (error) {
            alert('Failed to delete category: ' + error.message);
            return;
        }

        const updatedCategories = categories.filter(cat => cat.id !== categoryId);
        setCategories(updatedCategories);

        if (selectedCategory === categoryId) {
            setSelectedCategory(updatedCategories[0]?.id || null);
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                alert('Image must be smaller than 5MB');
                return;
            }

            setUploadImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUploadPost = async () => {
        if (!uploadImage || !uploadCaption.trim() || !selectedCategory) {
            alert('Please select an image, enter a caption, and select a category');
            return;
        }

        setUploading(true);
        setUploadStatus('Uploading image...');

        // Get category name for organizing images
        const category = categories.find(cat => cat.id === selectedCategory);

        // Upload image to Supabase Storage
        const { url, error: uploadError } = await uploadImageToStorage(uploadImage, category.name);

        if (uploadError) {
            alert('Failed to upload image: ' + uploadError.message);
            setUploading(false);
            setUploadStatus('');
            return;
        }

        setUploadStatus('Creating post...');

        // Add post to database
        const { data, error: postError } = await addPost(
            selectedCategory,
            url,
            uploadCaption.trim()
        );

        if (postError) {
            alert('Failed to create post: ' + postError.message);
            setUploading(false);
            setUploadStatus('');
            return;
        }

        // Add to local state
        setPosts([...posts, data]);

        setUploadStatus('✓ Post added successfully!');
        setUploadImage(null);
        setUploadCaption('');
        setImagePreview(null);
        setUploading(false);

        // Clear status after 3 seconds
        setTimeout(() => setUploadStatus(''), 3000);
    };

    const handleDeletePost = async (postId, imageUrl) => {
        if (!window.confirm('Are you sure you want to delete this post?')) {
            return;
        }

        // Delete post from database
        const { error: postError } = await deletePostService(postId);

        if (postError) {
            alert('Failed to delete post: ' + postError.message);
            return;
        }

        // Delete image from storage
        const { error: imageError } = await deleteImage(imageUrl);
        if (imageError) {
            console.error('Failed to delete image from storage:', imageError);
        }

        // Remove from local state
        setPosts(posts.filter(post => post.id !== postId));
    };

    const handleEditPost = (post) => {
        setEditingPost(post);
        setEditCaption(post.caption);
        setEditImagePreview(post.image_url);
        setEditImage(null);
    };

    const handleEditImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                alert('Image must be smaller than 5MB');
                return;
            }

            setEditImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUpdatePost = async () => {
        if (!editCaption.trim()) {
            alert('Please enter a caption');
            return;
        }

        const updates = {
            caption: editCaption.trim(),
        };

        // If a new image was selected, upload it
        if (editImage) {
            const category = categories.find(cat => cat.id === selectedCategory);

            // Upload new image
            const { url, error: uploadError } = await uploadImageToStorage(editImage, category.name);

            if (uploadError) {
                alert('Failed to upload image: ' + uploadError.message);
                return;
            }

            updates.image_url = url;

            // Delete old image
            await deleteImage(editingPost.image_url);
        }

        // Update post in database
        const { data, error } = await updatePost(editingPost.id, updates);

        if (error) {
            alert('Failed to update post: ' + error.message);
            return;
        }

        // Update local state
        setPosts(posts.map(post => post.id === editingPost.id ? data : post));

        // Close edit modal
        setEditingPost(null);
        setEditCaption('');
        setEditImage(null);
        setEditImagePreview(null);
    };

    const handleCancelEdit = () => {
        setEditingPost(null);
        setEditCaption('');
        setEditImage(null);
        setEditImagePreview(null);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = posts.findIndex(post => post.id === active.id);
            const newIndex = posts.findIndex(post => post.id === over.id);

            const newPosts = arrayMove(posts, oldIndex, newIndex);
            setPosts(newPosts);

            // Update display_order in database
            const postIds = newPosts.map(post => post.id);
            const { error } = await reorderPostsService(selectedCategory, postIds);

            if (error) {
                console.error('Failed to update order:', error);
                // Revert on error
                setPosts(posts);
            }
        }
    };

    const handleSignOut = async () => {
        if (window.confirm('Are you sure you want to sign out?')) {
            await signOut();
        }
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <p>Loading...</p>
            </div>
        );
    }

    const currentCategory = categories.find(cat => cat.id === selectedCategory);

    return (
        <div className="admin-container">
            <header className="admin-header">
                <h1>Gallery Admin Panel</h1>
                <div className="admin-header-actions">
                    <span className="admin-user-email">{user?.email}</span>
                    <button onClick={handleSignOut} className="admin-signout-btn">
                        🚪 Sign Out
                    </button>
                </div>
            </header>

            {error && <div className="admin-error-banner">{error}</div>}

            <div className="admin-content">
                {/* Category Management */}
                <section className="admin-section">
                    <h2>Categories</h2>
                    <div className="admin-category-add">
                        <input
                            type="text"
                            placeholder="New category name"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            className="admin-input"
                        />
                        <button onClick={handleAddCategory} className="admin-btn admin-btn-primary">
                            + Add Category
                        </button>
                    </div>

                    <div className="admin-category-list">
                        {categories.map((category) => (
                            <div
                                key={category.id}
                                className={`admin-category-item ${selectedCategory === category.id ? 'active' : ''}`}
                            >
                                {renamingCategory === category.id ? (
                                    <div className="admin-category-rename">
                                        <input
                                            type="text"
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            className="admin-input-small"
                                            autoFocus
                                        />
                                        <button
                                            onClick={() => handleRenameCategory(category.id)}
                                            className="admin-btn-icon"
                                        >
                                            ✓
                                        </button>
                                        <button
                                            onClick={() => {
                                                setRenamingCategory(null);
                                                setRenameValue('');
                                            }}
                                            className="admin-btn-icon"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <span
                                            className="admin-category-name"
                                            onClick={() => setSelectedCategory(category.id)}
                                        >
                                            {category.name} ({posts.filter(p => p.category_id === category.id).length})
                                        </span>
                                        <div className="admin-category-actions">
                                            <button
                                                onClick={() => {
                                                    setRenamingCategory(category.id);
                                                    setRenameValue(category.name);
                                                }}
                                                className="admin-btn-icon"
                                                title="Rename"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCategory(category.id)}
                                                className="admin-btn-icon"
                                                title="Delete"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* Upload Post */}
                {selectedCategory && (
                    <section className="admin-section">
                        <h2>Add Post to {currentCategory?.name}</h2>

                        <div className="admin-upload-form">
                            <div className="admin-upload-preview">
                                {imagePreview ? (
                                    <img src={imagePreview} alt="Preview" />
                                ) : (
                                    <div className="admin-upload-placeholder">
                                        <span>📷</span>
                                        <p>No image selected</p>
                                    </div>
                                )}
                            </div>

                            <div className="admin-upload-inputs">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageSelect}
                                    className="admin-file-input"
                                    id="imageUpload"
                                />
                                <label htmlFor="imageUpload" className="admin-file-label">
                                    Choose Image (Max 5MB)
                                </label>

                                <textarea
                                    placeholder="Enter caption..."
                                    value={uploadCaption}
                                    onChange={(e) => setUploadCaption(e.target.value)}
                                    className="admin-textarea"
                                    rows="3"
                                />

                                <button
                                    onClick={handleUploadPost}
                                    className="admin-btn admin-btn-success"
                                    disabled={!uploadImage || !uploadCaption.trim() || uploading}
                                >
                                    {uploading ? '⏳ Uploading...' : '➕ Add Post'}
                                </button>

                                {uploadStatus && (
                                    <p className="admin-upload-status">{uploadStatus}</p>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                {/* Post List with Drag-and-Drop */}
                {selectedCategory && currentCategory && (
                    <section className="admin-section">
                        <h2>Posts in {currentCategory.name}</h2>
                        <p className="admin-hint">Drag posts to reorder them</p>

                        {posts.length === 0 ? (
                            <p className="admin-empty">No posts yet. Add your first post above!</p>
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={posts.map(p => p.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="admin-post-list">
                                        {posts.map((post) => (
                                            <SortablePost
                                                key={post.id}
                                                post={post}
                                                onDelete={handleDeletePost}
                                                onEdit={handleEditPost}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        )}
                    </section>
                )}
            </div>

            <footer className="admin-footer">
                <p>✨ All changes are saved automatically to Supabase</p>
            </footer>

            {/* Edit Post Modal */}
            {editingPost && (
                <div className="admin-modal-overlay" onClick={handleCancelEdit}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Edit Post</h2>

                        <div className="admin-edit-form">
                            <div className="admin-upload-preview">
                                {editImagePreview ? (
                                    <img src={editImagePreview} alt="Preview" />
                                ) : (
                                    <div className="admin-upload-placeholder">
                                        <span>📷</span>
                                        <p>No image</p>
                                    </div>
                                )}
                            </div>

                            <div className="admin-upload-inputs">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleEditImageSelect}
                                    className="admin-file-input"
                                    id="editImageUpload"
                                />
                                <label htmlFor="editImageUpload" className="admin-file-label">
                                    {editImage ? 'Change Image' : 'Keep Current or Change Image'}
                                </label>

                                <textarea
                                    placeholder="Enter caption..."
                                    value={editCaption}
                                    onChange={(e) => setEditCaption(e.target.value)}
                                    className="admin-textarea"
                                    rows="3"
                                />

                                <div className="admin-modal-actions">
                                    <button
                                        onClick={handleUpdatePost}
                                        className="admin-btn admin-btn-success"
                                        disabled={!editCaption.trim()}
                                    >
                                        ✓ Save Changes
                                    </button>
                                    <button
                                        onClick={handleCancelEdit}
                                        className="admin-btn admin-btn-secondary"
                                    >
                                        ✕ Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin;
